import type { ReactNode } from 'react';

import type { DailyStat, IFormDailyStats } from '@feathr/blackbox';
import { moment, TimeFormat } from '@feathr/hooks';

import type { TDateRange } from '../DonationFormReport.utils';
import { formatCount, formatCurrency } from '../DonationFormReport.utils';

/**
 * Row in the chart-ready data array produced by `buildViewsChartData`. The
 * x-axis is the ISO date for the current-period day at position `i` (always
 * `currentRange[0] + i days`). Previous-period values are aligned by
 * day-of-range: day N of the previous range maps to day N of the current
 * range. Missing stat records become `null` (previous) or `0` (current).
 */
export interface IViewsChartRow {
  date: string;
  /** `num_views` for the current-period day at this position, or 0 if no stat record exists. */
  totalCurrent: number;
  /** `num_views` for the previous-period day at this position, or `null` if no
   * stat record exists (recharts skips null values). */
  totalPrevious: number | null;
  /** `unique_views` for the current-period day at this position, or 0 if no stat record exists. */
  uniqueCurrent: number;
  /** `unique_views` for the previous-period day at this position, or `null` if no
   * stat record exists (recharts skips null values). */
  uniquePrevious: number | null;
}

/**
 * Row in the chart-ready data array produced by `buildDonationAmountChartData`.
 * Same alignment rules as `IViewsChartRow`: x-axis dates are the current
 * range, previous values are mapped by day-of-range offset, and missing
 * records become `null` (previous) or `0` (current).
 */
export interface IDonationAmountChartRow {
  date: string;
  /** `donations_total` for the current-period day at this position, or 0. */
  amountCurrent: number;
  /** `donations_total` for the previous-period day at this position, or `null`
   * if no stat record exists (recharts skips null values). */
  amountPrevious: number | null;
}

/**
 * Row in the chart-ready data array produced by `buildDonationCountChartData`.
 * Same alignment rules as `IDonationAmountChartRow`.
 */
export interface IDonationCountChartRow {
  date: string;
  /** `donations_count` for the current-period day at this position, or 0. */
  countCurrent: number;
  /** `donations_count` for the previous-period day at this position, or `null`
   * if no stat record exists (recharts skips null values). */
  countPrevious: number | null;
}

function getMetadataDate(stat: DailyStat): string {
  const metadata = stat.get('metadata', undefined as never);
  const rawDate = metadata?.date;
  if (!rawDate) {
    return '';
  }
  return moment.utc(rawDate, moment.ISO_8601).format(TimeFormat.isoDate);
}

function getNumViews(stat: DailyStat): number {
  return stat.get('num_views', 0) as number;
}

function getUniqueViews(stat: DailyStat): number {
  const attrs = stat.attributes as Partial<IFormDailyStats>;
  return attrs.unique_views ?? 0;
}

function getDonationsTotal(stat: DailyStat): number {
  const attrs = stat.attributes as Partial<IFormDailyStats>;
  return attrs.donations_total ?? 0;
}

function getDonationsCount(stat: DailyStat): number {
  const attrs = stat.attributes as Partial<IFormDailyStats>;
  return attrs.donations_count ?? 0;
}

function indexStatsByDate(stats: readonly DailyStat[]): Map<string, DailyStat> {
  const byDate = new Map<string, DailyStat>();
  for (const stat of stats) {
    byDate.set(getMetadataDate(stat), stat);
  }
  return byDate;
}

/**
 * Filters a daily-stat array to the inclusive `[startISO, endISO]` window.
 *
 * Works around an anhinga bug where `GET /stats/?model=form&metadata__date__lte=...`
 * ignores the `__lte` bound and returns everything from `__gte` up to today. The
 * aggregated endpoint (`/stats/aggregated?model=form`) respects both bounds —
 * only the daily list endpoint is broken. Backend team is tracking the fix; once
 * shipped this helper (and its callers) can be deleted. See parent issue #6242
 * for context.
 */
function clipToRange(stats: readonly DailyStat[], range: TDateRange): DailyStat[] {
  const [start, end] = range;
  return stats.filter((stat) => {
    const date = getMetadataDate(stat);
    return date >= start && date <= end;
  });
}

/**
 * Generic helper that walks the current range day-by-day, paired with the
 * matching day-of-range offset in the previous range, and delegates row
 * construction to `makeRow`. Used by the three exported `build*ChartData`
 * functions — each one differs only in the row type and the field extractor.
 */
function buildDailyChartRows<TRow>(
  current: readonly DailyStat[],
  previous: readonly DailyStat[],
  currentRange: TDateRange,
  previousRange: TDateRange,
  makeRow: (args: {
    date: string;
    currentStat: DailyStat | undefined;
    previousStat: DailyStat | undefined;
  }) => TRow,
): TRow[] {
  const currentByDate = indexStatsByDate(clipToRange(current, currentRange));
  const previousByDate = indexStatsByDate(clipToRange(previous, previousRange));

  const currentCursor = moment.utc(currentRange[0], TimeFormat.isoDate);
  const previousCursor = moment.utc(previousRange[0], TimeFormat.isoDate);
  const currentEnd = moment.utc(currentRange[1], TimeFormat.isoDate);
  const length = currentEnd.diff(currentCursor, 'days') + 1;

  const rows: TRow[] = [];
  for (let i = 0; i < length; i += 1) {
    const xAxisDate = currentCursor.format(TimeFormat.isoDate);
    const previousDate = previousCursor.format(TimeFormat.isoDate);
    rows.push(
      makeRow({
        date: xAxisDate,
        currentStat: currentByDate.get(xAxisDate),
        previousStat: previousByDate.get(previousDate),
      }),
    );
    currentCursor.add(1, 'day');
    previousCursor.add(1, 'day');
  }

  return rows;
}

/**
 * Produces a chart-ready array aligning current-period daily stats with the
 * previous-period daily stats by day-of-range. For each day offset `i` from
 * the start of the current range, row `i` pairs the current stat for
 * `currentRange[0] + i days` with the previous stat for
 * `previousRange[0] + i days`. Missing records on either side become `0`
 * (current) or `null` (previous) without shifting the remaining values.
 *
 * The row count always equals the length of the current range, so the chart
 * spans the full window even when either side is sparse.
 *
 * @example
 * buildViewsChartData(current, previous, currentRange, previousRange)
 * // [{ date: '2026-04-14', totalCurrent: 12, totalPrevious: 10 }, ...]
 */
export function buildViewsChartData(
  current: readonly DailyStat[],
  previous: readonly DailyStat[],
  currentRange: TDateRange,
  previousRange: TDateRange,
): IViewsChartRow[] {
  return buildDailyChartRows(
    current,
    previous,
    currentRange,
    previousRange,
    ({ date, currentStat, previousStat }) => ({
      date,
      totalCurrent: currentStat ? getNumViews(currentStat) : 0,
      totalPrevious: previousStat ? getNumViews(previousStat) : null,
      uniqueCurrent: currentStat ? getUniqueViews(currentStat) : 0,
      uniquePrevious: previousStat ? getUniqueViews(previousStat) : null,
    }),
  );
}

/**
 * Produces a chart-ready array of daily donation totals aligning the current
 * and previous windows by day-of-range. See `buildViewsChartData` for the
 * alignment rules — only the metric field differs.
 */
export function buildDonationAmountChartData(
  current: readonly DailyStat[],
  previous: readonly DailyStat[],
  currentRange: TDateRange,
  previousRange: TDateRange,
): IDonationAmountChartRow[] {
  return buildDailyChartRows(
    current,
    previous,
    currentRange,
    previousRange,
    ({ date, currentStat, previousStat }) => ({
      date,
      amountCurrent: currentStat ? getDonationsTotal(currentStat) : 0,
      amountPrevious: previousStat ? getDonationsTotal(previousStat) : null,
    }),
  );
}

/**
 * Produces a chart-ready array of daily donation counts aligning the current
 * and previous windows by day-of-range. See `buildViewsChartData` for the
 * alignment rules — only the metric field differs.
 */
export function buildDonationCountChartData(
  current: readonly DailyStat[],
  previous: readonly DailyStat[],
  currentRange: TDateRange,
  previousRange: TDateRange,
): IDonationCountChartRow[] {
  return buildDailyChartRows(
    current,
    previous,
    currentRange,
    previousRange,
    ({ date, currentStat, previousStat }) => ({
      date,
      countCurrent: currentStat ? getDonationsCount(currentStat) : 0,
      countPrevious: previousStat ? getDonationsCount(previousStat) : null,
    }),
  );
}

/**
 * Formats an ISO date string (`YYYY-MM-DD`) as a short `MMM D` label for
 * recharts `tickFormatter` / `labelFormatter` props. Non-string values fall
 * through untouched so the chart stays resilient if recharts hands back a
 * numeric index.
 */
export function formatAxisDate(value: string | number): string {
  if (typeof value !== 'string' || !value) {
    return String(value);
  }
  return moment.utc(value, TimeFormat.isoDate).format('MMM D');
}

/**
 * Recharts `<Tooltip>` `formatter` for integer count values.
 */
export function formatCountTooltipValue(
  value: number | string | Array<number | string>,
): ReactNode {
  if (typeof value === 'number') {
    return formatCount(value);
  }
  return String(value);
}

/**
 * Recharts `<Tooltip>` `formatter` for currency values.
 */
export function formatCurrencyTooltipValue(
  value: number | string | Array<number | string>,
): ReactNode {
  if (typeof value === 'number') {
    return formatCurrency(value);
  }
  return String(value);
}
